import React, { useContext, FC } from 'react';
import { ThemeContext } from 'styled-components';
import { NetworkStatus, useQuery } from '@apollo/client';
import { get, map } from 'lodash';
import {
  WorkPackage,
  WorkPackageFilterInput,
  PagingInput,
  WorkPackageSearchByEnum,
  WorkPackageFilterByTerm,
  WorkPackageSortByTerm,
  CrewMember,
  SortOrder,
} from '@bechtel/types';
import {
  Box,
  Heading,
  Text,
  Spinner,
  SpinnerSize,
  Button,
  Icon,
  useMediaQuery,
  belowBreakpoint,
  Theme,
} from '@bechtel/gds';
import { ErrorBoundary, WorkPackageItemCard, useModulePermission } from '@bechtel/client-common';
import { GET_WORKPACKAGES } from '@bechtel/client-gql';
import { WorkpackageListStyled, HeaderStyled, NavLinkStyled, WorkPackageItemStyled } from './styled';
import { drawerValues as defaultValues } from '@bechtel/client-work-packages';
import { Trans } from '@lingui/macro';

interface WorkPackageListProps {
  crewMember: CrewMember | null | undefined;
}

export const WorkpackageList: FC<WorkPackageListProps> = ({ crewMember }) => {
  const { breakpoints } = useContext<Theme>(ThemeContext);
  let filter: WorkPackageFilterInput = {
    searchBy: [{ type: WorkPackageSearchByEnum.WP_NUMBER }],
    filterBy: WorkPackageFilterByTerm.IN_3WLA,
    sortBy: WorkPackageSortByTerm.WP_NUMBER,
    assignedToMe: true,
    sortOrder: SortOrder.DESC,
  };

  if (crewMember && crewMember.id) {
    filter = {
      ...filter,
      assignedToMe: false,
      filterByOwners: [{ persona: crewMember?.persona, crewMemberId: crewMember?.id }],
    };
  }

  const isWideScreen = useMediaQuery(belowBreakpoint(breakpoints.widescreen));
  const { loading, data, error, refetch, networkStatus } = useQuery<{
    filter: WorkPackageFilterInput;
    paging: PagingInput;
  }>(GET_WORKPACKAGES, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ...filter,
      },
      paging: {
        cursor: '1',
        limit: 10,
      },
    },
  });

  const workPackageList = get(data, 'workPackages.items', []);

  const { canViewWorkPackageModule } = useModulePermission();

  return (
    <WorkpackageListStyled>
      <HeaderStyled>
        <Heading size={isWideScreen ? '500' : '600'} weight="600">
          <Trans>Work Packages-3WLA</Trans>
        </Heading>
      </HeaderStyled>
      <Box flexDirection={{ sm: 'column' }} style={{ overflowY: 'auto', height: '100%' }}>
        {(networkStatus === NetworkStatus.refetch || loading) && (
          <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }} css="height: 100%;">
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!loading && error && (
          <Box
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            flexGrow={{ sm: 1 }}
            flexDirection={{ sm: 'column' }}
            p={{ sm: '500' }}
          >
            <Box pb={{ sm: '500' }}>
              <Text>
                <Trans>There was a problem loading Work Packages. Please click the button below to try again.</Trans>
              </Text>
            </Box>
            <Button onClick={() => refetch()}>
              <Box mr={{ sm: '400' }}>
                <Icon icon="undo" />
              </Box>
              <Trans>Retry</Trans>
            </Button>
          </Box>
        )}
        {!loading &&
          !error &&
          (workPackageList.length > 0 ? (
            map(workPackageList, (wp: WorkPackage) => {
              const encodedId = encodeURIComponent(wp.id);
              return canViewWorkPackageModule ? (
                <NavLinkStyled
                  to={{
                    pathname: `/work-packages/${encodedId}`,
                    search: `?tag=${wp.tag}`,
                  }}
                  state={{
                    drawerValues: { ...defaultValues, filterBy: WorkPackageFilterByTerm.IN_3WLA, searchScope: false },
                  }}
                  key={encodedId}
                >
                  <ErrorBoundary>
                    <WorkPackageItemCard {...wp} />
                  </ErrorBoundary>
                </NavLinkStyled>
              ) : (
                <WorkPackageItemStyled>
                  <ErrorBoundary>
                    <WorkPackageItemCard {...wp} />
                  </ErrorBoundary>
                </WorkPackageItemStyled>
              );
            })
          ) : (
            <Box
              flexGrow={{ sm: 1 }}
              flexDirection={{ sm: 'column' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              // pt={{ sm: '900' }}
              data-testid="workpackage-list-empty"
            >
              <Heading size="400" weight="600" color="grey.600">
                <Trans>No work packages found.</Trans>
              </Heading>
              <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
                <Text
                  size="400"
                  family="graphik"
                  color="grey.600"
                  //  line height is required because when sentence wraps in two lines gap between both lines are required.
                  style={{ textAlign: 'center', lineHeight: '1.57' }}
                >
                  <Trans>You do not have any workpackage(s) assigned to you which are planned in 3WLA.</Trans>
                </Text>
              </Box>
            </Box>
          ))}
      </Box>
    </WorkpackageListStyled>
  );
};
